import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../store';
import { chatKeys } from '../../hooks/chat/useChatQueries';
import { useMobileBackClose } from '../../hooks/chat/useMobileBackClose';
import { ChatInbox } from './ChatInbox';
import { ChatChannel } from './ChatChannel';
import { NewMessage } from './NewMessage';
import { MembersView } from './MembersView';
import { AnnouncersView } from './AnnouncersView';

function isTextEntry(el: Element | null): boolean {
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    (el instanceof HTMLElement && el.isContentEditable)
  );
}

/**
 * Hand-rolled like the mobile nav drawer in Layout.tsx, not the design-system
 * Sheet: Sheet adds a scrim and closes on outside click, and chat must stay
 * open while the person works in the chart (EMR-1818 decision 27).
 */
export function ChatPanel() {
  const isChatOpen = useStore(state => state.isChatOpen);
  const chatView = useStore(state => state.chatView);
  const activeChannelId = useStore(state => state.activeChannelId);
  const closeChat = useStore(state => state.closeChat);
  const setChatView = useStore(state => state.setChatView);
  const queryClient = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  useMobileBackClose();

  useEffect(() => {
    if (!isChatOpen) return;
    // Data flow: the inbox is refetched on panel open. The 30 s staleTime would
    // otherwise serve the rows fetched when the launcher mounted.
    void queryClient.invalidateQueries({ queryKey: chatKeys.inbox() });
    ref.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      // Radix menus call preventDefault on the Escape they consume, so a
      // prevented Escape belongs to the menu and the panel keeps the next one.
      if (e.defaultPrevented) return;
      // Esc and N belong to the panel only: a key typed in a chart control
      // outside the panel must not act on chat.
      if (!ref.current?.contains(document.activeElement)) return;
      if (e.key === 'Escape') {
        closeChat();
        return;
      }
      if (
        (e.key === 'n' || e.key === 'N') &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !isTextEntry(document.activeElement)
      ) {
        // Prevented so the autofocused To input does not receive the "n".
        e.preventDefault();
        setChatView('new');
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isChatOpen, closeChat, setChatView, queryClient]);

  // A click on an inbox row, Back, or a menu item (Members, Announcers)
  // unmounts the focused element with the old view, and focus falls to body.
  // The Esc guard above would then ignore the key, so put focus back on the
  // panel div (not the composer: that opens the phone keyboard). Focus
  // anywhere else stays: New message keeps its autofocused To input, and a
  // chart control keeps focus when a 404 sends the view back to the inbox
  // while the person types in the chart.
  useEffect(() => {
    const active = document.activeElement;
    if (!active || active === document.body) ref.current?.focus();
  }, [chatView, activeChannelId]);

  if (!isChatOpen) return null;

  // Each view renders the connection strip under its own header.
  return (
    <div
      ref={ref}
      role='complementary'
      aria-label='Messages'
      tabIndex={-1}
      className='fixed inset-0 md:inset-y-0 md:left-auto md:right-0 md:w-[25rem] z-40 flex flex-col border-l bg-background'
    >
      {chatView === 'inbox' && <ChatInbox />}
      {/* Keyed by the channel id: the chart's care-team button can change
          `activeChannelId` without this view ever unmounting (it does not
          route through the inbox), and a fresh key resets ChatChannel's own
          per-send state (e.g. an earlier archived-on-409 flag) so it never
          carries over to the next channel. */}
      {chatView === 'channel' && (
        <ChatChannel key={activeChannelId ?? undefined} />
      )}
      {chatView === 'new' && <NewMessage />}
      {chatView === 'members' && <MembersView />}
      {chatView === 'announcers' && <AnnouncersView />}
    </div>
  );
}
